import { computed, inject, InjectionToken, Service, signal } from '@angular/core'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { environment, version } from '@awork/environments/environment'

export type MobileContentVersion = { kind: 'builtin' } | { kind: 'ota'; version: string } | { kind: 'unknown' }

export interface MobileVersionInfo {
  nativeVersion: string
  nativeBuild: string | null
  content: MobileContentVersion
}

export interface MobileVersionRuntime {
  /** Returns whether the app runs through a native Capacitor shell. */
  isNativePlatform(): boolean

  /** Reads metadata from the installed native app. */
  getAppInfo(): ReturnType<typeof App.getInfo>

  /** Reads the active Capgo web bundle. */
  getCurrentBundle(): ReturnType<typeof CapacitorUpdater.current>
}

export const MOBILE_VERSION_RUNTIME = new InjectionToken<MobileVersionRuntime>('MOBILE_VERSION_RUNTIME', {
  providedIn: 'root',
  factory: () => ({
    isNativePlatform: () => Capacitor.isNativePlatform(),
    getAppInfo: () => App.getInfo(),
    getCurrentBundle: () => CapacitorUpdater.current()
  })
})

const initialVersionInfo: MobileVersionInfo = {
  nativeVersion: version,
  nativeBuild: null,
  content: { kind: 'builtin' }
}

/** Provides the native and active web-bundle versions for diagnostics. */
@Service()
export class MobileVersionService {
  private readonly runtime = inject(MOBILE_VERSION_RUNTIME)
  private readonly versionInfoState = signal(initialVersionInfo)
  private initialization: Promise<MobileVersionInfo> | undefined

  readonly versionInfo = this.versionInfoState.asReadonly()
  readonly diagnosticText = computed(() => this.createDiagnosticText())

  /**
   * Reads native version information once and reuses the result for all consumers.
   * @returns {Promise<MobileVersionInfo>}
   */
  initialize(): Promise<MobileVersionInfo> {
    this.initialization ??= this.loadVersionInfo()
    return this.initialization
  }

  /**
   * Builds a stable diagnostic block for support tools.
   * @withSignal versionInfoState
   * @returns {string}
   */
  private createDiagnosticText(): string {
    const info = this.versionInfoState()
    const contentVersion = info.content.kind === 'ota' ? info.content.version : info.content.kind

    return [
      'awork mobile',
      `environment=${environment}`,
      `nativeVersion=${info.nativeVersion}`,
      `nativeBuild=${info.nativeBuild ?? 'unknown'}`,
      `contentVersion=${contentVersion}`
    ].join('\n')
  }

  /**
   * Reads plugin state without making app startup depend on diagnostic availability.
   * @returns {Promise<MobileVersionInfo>}
   */
  private async loadVersionInfo(): Promise<MobileVersionInfo> {
    if (!this.runtime.isNativePlatform()) {
      return this.versionInfoState()
    }

    const [appResult, updaterResult] = await Promise.allSettled([
      this.runtime.getAppInfo(),
      this.runtime.getCurrentBundle()
    ])

    if (appResult.status === 'rejected') {
      console.warn('Could not read the native app version', appResult.reason)
    }

    if (updaterResult.status === 'rejected') {
      console.warn('Could not read the mobile content version', updaterResult.reason)
    }

    const appInfo = appResult.status === 'fulfilled' ? appResult.value : undefined
    const content = this.resolveContentVersion(updaterResult)
    const info: MobileVersionInfo = {
      nativeVersion: appInfo?.version ?? version,
      nativeBuild: appInfo?.build ?? null,
      content
    }

    this.versionInfoState.set(info)

    return info
  }

  /**
   * Maps the updater result to a value that does not confuse lookup failures with the built-in bundle.
   * @param {PromiseSettledResult<Awaited<ReturnType<typeof CapacitorUpdater.current>>>} result
   * @returns {MobileContentVersion}
   */
  private resolveContentVersion(
    result: PromiseSettledResult<Awaited<ReturnType<typeof CapacitorUpdater.current>>>
  ): MobileContentVersion {
    if (result.status === 'rejected') {
      return { kind: 'unknown' }
    }

    const { bundle } = result.value

    if (bundle.id === 'builtin') {
      return { kind: 'builtin' }
    }

    if (!bundle.version) {
      return { kind: 'unknown' }
    }

    return { kind: 'ota', version: bundle.version }
  }
}
